import { useFormContext } from '@iheartjane/reefer-hook-form';
import type { KindCondition, ProductKind } from '@iheartjane/types';
import compact from 'lodash-es/compact';
import get from 'lodash-es/get';
import join from 'lodash-es/join';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { ConfirmWrapperWithTracking } from '@jane/business-admin/components/ConfirmWrapperWithTracking';
import { convertRulesToString } from '@jane/business-admin/components/shared/specials/modal/conditions/convertRulesToString';
import { ConditionByModal } from '@jane/business-admin/components/shared/specials/modal/conditions/generic_components/ConditionByModal';
import { LoadingCondition } from '@jane/business-admin/components/shared/specials/modal/conditions/generic_components/LoadingCondition';
import { ConditionType } from '@jane/business-admin/components/shared/specials/modal/form';
import type { StoreCategory } from '@jane/business-admin/data-access';
import { useCategoriesForRulesets } from '@jane/business-admin/data-access';
import { useRetailOrgSettings, useStoreId } from '@jane/business-admin/hooks';
import { ModalNames } from '@jane/business-admin/util';
import { encodeKey } from '@jane/shared-b2b/components';
import { isSoldByWeight } from '@jane/shared/util';

import { CategorySelectInputModal } from './CategorySelectInputModal';
import { CategorySelectInputModalProvider } from './CategorySelectInputModalProvider';
import { convertCategoryConditionToRules, transformData } from './util/rules';

export const CategoryConditions = ({
  disableAddButton,
  fieldPrefix,
  footer,
  isGlobalSpecial,
  type,
  onConditionsUpdate,
}: {
  disableAddButton?: boolean;
  fieldPrefix: string;
  footer?: ReactNode;
  isGlobalSpecial?: boolean;
  onConditionsUpdate: (
    conditions: KindCondition | [],
    includesOrExcludes: 'includes' | 'excludes'
  ) => void;
  type: ConditionType.Categories | ConditionType.ExcludeCategories;
}) => {
  const storeId = useStoreId();
  const [categoryConditionsModalOpen, setCategoryConditionsModalOpen] =
    useState(false);
  const [selectedCategories, setSelectedCategories] = useState<StoreCategory[]>(
    []
  );
  const { orgId } = useRetailOrgSettings();
  const { data: allCategories, isFetching } = useCategoriesForRulesets({
    isGlobal: Boolean(isGlobalSpecial),
    orgId,
    storeId,
  });

  const { watch } = useFormContext();

  const rulesWatch = watch(`${fieldPrefix}.rules`);
  const conditionsWatch = watch(`conditions`);
  const specialTypeWatch = watch('special_type');

  const includesOrExcludes =
    type === ConditionType.Categories ? 'includes' : 'excludes';

  const rules =
    rulesWatch && rulesWatch[includesOrExcludes]
      ? rulesWatch[includesOrExcludes]
      : null;

  const categoryMap = useMemo(() => {
    return get(rulesWatch, `${includesOrExcludes}.0.kinds`, []);
  }, [convertRulesToString(rulesWatch), JSON.stringify(conditionsWatch)]);

  const categoryOptions = useMemo(() => {
    if (!allCategories) return [];

    let renderedKinds = allCategories;

    if (specialTypeWatch === 'bulk_pricing') {
      renderedKinds = allCategories.filter(
        (k): k is { kind: ProductKind; subtypes: any[] } => {
          return isSoldByWeight(k.kind as ProductKind);
        }
      );
    }

    return transformData(renderedKinds);
  }, [storeId, allCategories, specialTypeWatch]);

  const selectedCategoryWithDetails = allCategories?.filter(
    (category: StoreCategory) => categoryMap?.includes(category)
  );

  const conditionsToDisplay = categoryMap?.reduce(
    (acc: any[], category: any) => {
      if (
        specialTypeWatch === 'bulk_pricing' &&
        !isSoldByWeight(category.kind)
      ) {
        return acc;
      }

      const parts = compact([
        category.kind,
        category.root_subtype,
        category.brand_subtype,
      ]);

      acc.push({ value: encodeKey(parts), label: join(parts, '/') });

      return acc;
    },
    []
  );

  const onSubmit = (data: any) => {
    const formattedKinds = convertCategoryConditionToRules(data);
    onConditionsUpdate(formattedKinds, includesOrExcludes);

    setSelectedCategories(conditionsToDisplay);
    setCategoryConditionsModalOpen(false);
  };

  useEffect(() => {
    if (!selectedCategoryWithDetails) {
      return;
    }

    setSelectedCategories(selectedCategoryWithDetails);
  }, [JSON.stringify(selectedCategoryWithDetails)]);

  return (
    <>
      {!conditionsToDisplay ? (
        <LoadingCondition />
      ) : (
        <ConditionByModal
          disableAddButton={disableAddButton}
          footer={footer}
          onOpenModal={() => setCategoryConditionsModalOpen(true)}
          type={type}
          conditions={conditionsToDisplay}
          conditionsLoading={isFetching}
          onConditionsUpdate={(data) => onSubmit(data)}
        />
      )}
      <ConfirmWrapperWithTracking
        open={categoryConditionsModalOpen}
        setOpen={() => {
          setCategoryConditionsModalOpen(false);
        }}
        hasChanges={selectedCategories.length !== conditionsToDisplay?.length}
        modalName={ModalNames.CategoryConditions}
        overlayClose={true}
      >
        <CategorySelectInputModalProvider rules={rules?.[0]?.kinds}>
          <CategorySelectInputModal
            categoryOptions={categoryOptions}
            onSave={onSubmit}
          />
        </CategorySelectInputModalProvider>
      </ConfirmWrapperWithTracking>
    </>
  );
};
